import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import axios, { bumpAuthEpoch } from '../utils/axios';
import { clearOfflineData } from '../offline/offlineStorage';
import { clearAllMasterData } from '../offline/localDatabase';
import { syncAllMasterData, startBackgroundSync, stopBackgroundSync } from '../offline/syncManager';
import { canPerform } from '../utils/effectivePermissions';
import { getRoleHomePath } from '../config/roles';
import { resolvePdfCompany, setPdfBrand } from '../utils/pdf/pdfBrand';
import { formatApiError } from '../utils/apiError';

const AuthContext = createContext(null);

function readStoredJson(key) {
    try {
        const raw = localStorage.getItem(key);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    // Sync hydrate from localStorage so direct/new-tab URLs have session on first paint
    const [user, setUser] = useState(() => readStoredJson('user'));
    const [business, setBusiness] = useState(() => readStoredJson('business'));
    const [company, setCompany] = useState(() => readStoredJson('company'));
    const [distributor, setDistributor] = useState(() => readStoredJson('distributor'));
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    // Session is sync-hydrated above; no blocking spinner on first paint.
    const [loading, setLoading] = useState(false);
    const [syncIntervalId, setSyncIntervalId] = useState(null);

    // Validate hydrated session and warm offline cache
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        const storedUser = readStoredJson('user');

        if (storedToken && !storedUser) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('business');
            localStorage.removeItem('company');
            localStorage.removeItem('distributor');
            setToken(null);
            setUser(null);
            setBusiness(null);
            setCompany(null);
            setDistributor(null);
            return;
        }

        if (storedToken && storedUser) {
            // Re-sync in case another tab updated storage; state already hydrated for first paint
            setToken(storedToken);
            setUser(storedUser);
            setBusiness(readStoredJson('business'));
            setCompany(readStoredJson('company'));
            setDistributor(readStoredJson('distributor'));

            if (typeof navigator === 'undefined' || navigator.onLine) {
                syncAllMasterData().catch((err) => {
                    console.warn('Session restore sync failed:', err);
                });
                const intervalId = startBackgroundSync(15);
                setSyncIntervalId(intervalId);
            }
        }
    }, []);

    // Pull the current user's access control state from the server so permission changes
    // made by an admin take effect without waiting for a re-login.
    const refreshSession = useCallback(async () => {
        if (!localStorage.getItem('token')) return null;
        try {
            const { data } = await axios.get('/auth/me');
            const fresh = data?.data;
            if (!fresh?.user) return null;
            // Only the user record is refreshed for auth. Tenant display fields (name, logo,
            // print settings) are also synced so document branding stays current.
            setUser(fresh.user);
            localStorage.setItem('user', JSON.stringify(fresh.user));
            if (fresh.company) {
                setCompany(fresh.company);
                localStorage.setItem('company', JSON.stringify(fresh.company));
            }
            if (fresh.distributor) {
                setDistributor(fresh.distributor);
                localStorage.setItem('distributor', JSON.stringify(fresh.distributor));
            }
            if (fresh.business) {
                setBusiness(fresh.business);
                localStorage.setItem('business', JSON.stringify(fresh.business));
            }
            return fresh.user;
        } catch {
            // Offline or transient failure — keep the hydrated session as-is
            return null;
        }
    }, []);

    // Apply an authenticated session (token + user + tenant context) to state,
    // localStorage, and kick off data sync. Shared by login and OTP verification.
    const applySession = useCallback((data) => {
        bumpAuthEpoch();
        const { token, user, business, company, distributor } = data;

        setToken(token);
        setUser(user);
        setBusiness(business || null); // Legacy support
        setCompany(company || null);
        setDistributor(distributor || null);

        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        if (business) {
            localStorage.setItem('business', JSON.stringify(business));
        } else {
            localStorage.removeItem('business');
        }
        if (company) {
            localStorage.setItem('company', JSON.stringify(company));
        } else {
            localStorage.removeItem('company');
        }
        if (distributor) {
            localStorage.setItem('distributor', JSON.stringify(distributor));
        } else {
            localStorage.removeItem('distributor');
        }

        // Sync all master data (non-blocking)
        syncAllMasterData().catch(err => {
            console.warn('Initial sync failed:', err);
        });

        // Background master sync (15 min; page-level useLocalData covers active screens)
        const intervalId = startBackgroundSync(15);
        setSyncIntervalId(intervalId);
    }, []);

    const login = useCallback(async (loginId, password) => {
        try {
            const response = await axios.post('/auth/login', {
                login: String(loginId || '').trim(),
                password,
            });
            applySession(response.data.data);
            return { success: true };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                error: formatApiError(error, 'Could not sign in'),
                needsVerification: !!data?.needsVerification,
                email: data?.email,
            };
        }
    }, [applySession]);

    // Registration no longer logs the user in — it triggers email OTP verification.
    const register = useCallback(async (formData) => {
        try {
            const response = await axios.post('/auth/register-business', formData);
            return {
                success: true,
                requiresVerification: true,
                email: response.data.data?.email || formData.email,
            };
        } catch (error) {
            return {
                success: false,
                error: formatApiError(error, 'Could not complete registration'),
            };
        }
    }, []);

    // Verify the email OTP; on success the user is signed in.
    const verifyOTP = useCallback(async (email, otp) => {
        try {
            const response = await axios.post('/auth/verify-otp', { email, otp });
            applySession(response.data.data);
            return { success: true };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                error: formatApiError(error, 'Could not verify code'),
                alreadyVerified: !!data?.alreadyVerified,
            };
        }
    }, [applySession]);

    const resendOTP = useCallback(async (email) => {
        try {
            const response = await axios.post('/auth/resend-otp', { email });
            return { success: true, message: response.data.message };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                error: formatApiError(error, 'Could not resend verification code'),
                alreadyVerified: !!data?.alreadyVerified,
            };
        }
    }, []);

    const forgotPassword = useCallback(async (email) => {
        try {
            const response = await axios.post('/auth/forgot-password', { email });
            return {
                success: true,
                message: response.data.message,
                email: response.data.data?.email || email,
            };
        } catch (error) {
            const data = error.response?.data;
            return {
                success: false,
                code: data?.code || null,
                contactAdmin: data?.code === 'CONTACT_ADMIN',
                error: formatApiError(error, 'Could not send reset code'),
            };
        }
    }, []);

    const resendPasswordResetOTP = useCallback(async (email) => {
        try {
            const response = await axios.post('/auth/resend-reset-otp', { email });
            return { success: true, message: response.data.message };
        } catch (error) {
            return {
                success: false,
                error: formatApiError(error, 'Could not resend reset code'),
            };
        }
    }, []);

    const resetPassword = useCallback(async (email, otp, password) => {
        try {
            const response = await axios.post('/auth/reset-password', { email, otp, password });
            applySession(response.data.data);
            return { success: true, message: response.data.message };
        } catch (error) {
            return {
                success: false,
                error: formatApiError(error, 'Could not reset password'),
            };
        }
    }, [applySession]);

    const logout = useCallback(async () => {
        bumpAuthEpoch();
        // Stop background sync
        if (syncIntervalId) {
            stopBackgroundSync(syncIntervalId);
            setSyncIntervalId(null);
        }

        setToken(null);
        setUser(null);
        setBusiness(null);
        setCompany(null);
        setDistributor(null);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('business');
        localStorage.removeItem('company');
        localStorage.removeItem('distributor');

        // Clear all offline data
        await clearOfflineData();
        await clearAllMasterData();
    }, [syncIntervalId]);

    // Keep PDF branding in sync with the signed-in business context
    useEffect(() => {
        setPdfBrand(resolvePdfCompany({ company, distributor, business }));
    }, [company, distributor, business]);

    // Re-read permissions on load and whenever the tab regains focus
    useEffect(() => {
        if (!localStorage.getItem('token')) return undefined;
        refreshSession();
        const onFocus = () => refreshSession();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [refreshSession]);

    /** Merge self-service profile fields into the live session without re-login. */
    const applyUserUpdate = useCallback((partial) => {
        if (!partial || typeof partial !== 'object') return;
        setUser((prev) => {
            const next = { ...(prev || {}), ...partial };
            // Prefer canonical id from either shape
            if (partial.id && !next._id) next._id = partial.id;
            if (partial._id && !next.id) next.id = partial._id;
            try {
                localStorage.setItem('user', JSON.stringify(next));
            } catch {
                /* ignore quota */
            }
            return next;
        });
    }, []);

    /** Apply profile save session payload (user + tenant display names). */
    const applyProfileSession = useCallback((session) => {
        if (!session || typeof session !== 'object') return;
        if (session.user) applyUserUpdate(session.user);
        if (session.company) {
            setCompany((prev) => {
                const next = { ...(prev || {}), ...session.company };
                if (session.company.id && !next._id) next._id = session.company.id;
                try { localStorage.setItem('company', JSON.stringify(next)); } catch { /* ignore */ }
                return next;
            });
        }
        if (session.distributor) {
            setDistributor((prev) => {
                const next = { ...(prev || {}), ...session.distributor };
                if (session.distributor.id && !next._id) next._id = session.distributor.id;
                try { localStorage.setItem('distributor', JSON.stringify(next)); } catch { /* ignore */ }
                return next;
            });
        }
        if (session.business) {
            setBusiness((prev) => {
                const next = { ...(prev || {}), ...session.business };
                if (session.business.id && !next._id) next._id = session.business.id;
                try { localStorage.setItem('business', JSON.stringify(next)); } catch { /* ignore */ }
                return next;
            });
        }
    }, [applyUserUpdate]);

    // RBAC: can(module, action) — Owner/SaaSAdmin bypass; KPO inherits parent admin permissions.
    const can = useCallback((module, action) => canPerform(user, module, action), [user]);

    const homePath = useMemo(() => getRoleHomePath(user?.role), [user?.role]);

    const value = useMemo(() => ({
        user,
        business,
        company,
        distributor,
        token,
        loading,
        login,
        register,
        verifyOTP,
        resendOTP,
        forgotPassword,
        resendPasswordResetOTP,
        resetPassword,
        logout,
        refreshSession,
        applyUserUpdate,
        applyProfileSession,
        can,
        homePath,
        isAuthenticated: !!token,
    }), [user, business, company, distributor, token, loading, login, register, verifyOTP, resendOTP, forgotPassword, resendPasswordResetOTP, resetPassword, logout, refreshSession, applyUserUpdate, applyProfileSession, can, homePath]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
